import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  pointerWithin,
  CollisionDetection,
  PointerSensorOptions,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { PropsWithChildren } from 'react'

// data-no-dnd属性を持つ要素からのドラッグを無効化するカスタムセンサー
class NoDndPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (
        { nativeEvent: event }: { nativeEvent: PointerEvent },
        { onActivation }: PointerSensorOptions
      ) => {
        const target = event.target as HTMLElement
        if (target.closest('[data-no-dnd="true"]')) {
          return false
        }
        onActivation?.({ event })
        return true
      },
    },
  ]
}

// 左クリック（メインボタン）のみでドラッグを開始するカスタムセンサー
class PrimaryButtonOnlyPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: (
        { nativeEvent: event }: { nativeEvent: PointerEvent },
        { onActivation }: PointerSensorOptions
      ) => {
        if (event.button !== 0) {
          return false
        }
        const target = event.target as HTMLElement
        if (target.closest('[data-no-dnd="true"]')) {
          return false
        }
        onActivation?.({ event })
        return true
      },
    },
  ]
}

export type Props = PropsWithChildren<{
  onDragStart?: (event: DragStartEvent) => void
  onDragEnd: (event: DragEndEvent) => void
  onDragOver?: (event: DragOverEvent) => void
  delay?: number
  collisionDetection?: CollisionDetection
  primaryButtonOnly?: boolean
}>

const DndComponent = ({
  onDragStart,
  onDragEnd,
  onDragOver,
  children,
  delay = 120,
  collisionDetection = pointerWithin,
  primaryButtonOnly = false,
}: Props) => {
  const SensorClass = primaryButtonOnly
    ? PrimaryButtonOnlyPointerSensor
    : NoDndPointerSensor
  const sensors = useSensors(
    useSensor(SensorClass, {
      activationConstraint: {
        delay,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
    >
      {children}
    </DndContext>
  )
}

export default DndComponent
